import React, { useMemo, useState } from 'react';
import { Grid, Theme, Typography, useMediaQuery } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'i18n';
import { formatPrice } from 'utils/format';
import LeaderMenuItem from 'components/common/LeaderMenuItem';
import ExpandAction from 'components/common/ExpandAction';
import ToolTipIcon from 'components/svg/ToolTipIcon.svg';
import OrderSummaryItems from './OrderSummaryItems';
import TooltipInfo from '../../admin/TooltipInfo';

interface IProps {
  tickets: any[];
  addons: any[];
  ticketsCounts: Record<string, number>;
  addonsCounts: Record<string, number>;
  totalTicketsCount: number;
  totalAddonsCount: number;
  totalTicketsPrice: number;
  totalAddonsPrice: number;
  totalDonations: number;
  totalDiscount: number;
  ticketsFeeCoverage: string | null;
  paidTicketsCount: number;
  processingFee: number;
  processingFeeLabel: string;
  totalPrice: number;
  taxes: number;
  currency: string;
  notExpandable?: boolean;
  isPaidSubscriptionFeeByBuyer?: boolean;
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const useStyles = makeStyles()((theme) => ({
  h5: {
    ...theme.typography.h5,
    [theme.breakpoints.down('md')]: {
      ...theme.typography.h5,
    },
  },
  infoIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    // marginLeft: theme.spacing(1), // space from label
    verticalAlign: 'middle',
    cursor: 'pointer',
    paddingBottom: '4px',
  },
}));

const formatTaxes = (taxes: number): string => taxes.toFixed(2);

const OrderSummary: React.FC<React.PropsWithChildren<IProps>> = ({
  tickets,
  addons,
  ticketsCounts,
  addonsCounts,
  totalTicketsCount,
  totalAddonsCount,
  totalTicketsPrice,
  totalAddonsPrice,
  totalDonations,
  totalDiscount,
  ticketsFeeCoverage,
  paidTicketsCount,
  processingFee,
  processingFeeLabel,
  totalPrice,
  taxes,
  currency,
  notExpandable,
  isPaidSubscriptionFeeByBuyer,
}) => {
  const { classes } = useStyles();
  const [expanded, setExpanded] = useState(false);
  const [t] = useTranslation('public');
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'));

  const ticketsFeeSufix = useMemo(() => {
    if (ticketsFeeCoverage === 'host-covers-fees-disclosed') return '(paid by host)';
    if (ticketsFeeCoverage === 'buyer-can-decide' && !isPaidSubscriptionFeeByBuyer) return '(paid by host)';
    return '';
  }, [isPaidSubscriptionFeeByBuyer, ticketsFeeCoverage]);

  const handleOnClick = () => window.open('https://help.jlive.app/jlive-fees/', '_blank');

  return (
    <Grid container direction="column" spacing={3}>
      {!notExpandable && (
        <Grid item container spacing={1} alignItems="center">
          <Grid item xs>
            <Typography
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              className={classes.h5}
              variant="h5"
            >
              {t<string>('creatingOrder.orderSummary.title')}
            </Typography>
          </Grid>
          <Grid item>
            <ExpandAction edge="end" size="small" expanded={expanded} onExpandChange={setExpanded} />
          </Grid>
        </Grid>
      )}
      <Grid item container direction="column" spacing={2}>
        <Grid item>
          <OrderSummaryItems
            itemType="ticket"
            currency={currency}
            expanded={expanded}
            orderEntities={tickets}
            orderCounts={ticketsCounts}
            totalCount={totalTicketsCount}
            totalPrice={totalTicketsPrice}
          />
        </Grid>
        {totalAddonsCount > 0 && (
          <Grid item>
            <OrderSummaryItems
              itemType="addon"
              currency={currency}
              expanded={expanded}
              orderEntities={addons}
              orderCounts={addonsCounts}
              totalCount={totalAddonsCount}
              totalPrice={totalAddonsPrice}
            />
          </Grid>
        )}
        <Grid item>
          <LeaderMenuItem>
            <Typography variant="subtitle2" fontWeight={500}>
              {t<string>('common:general.taxes')}
            </Typography>
            <Typography variant="subtitle2" fontWeight={500}>
              {formatPrice(formatTaxes(taxes), currency, false, false)}
            </Typography>
          </LeaderMenuItem>
        </Grid>
        {totalDiscount > 0 && (
          <Grid item>
            <LeaderMenuItem>
              <Typography variant="subtitle2" fontWeight={500}>
                {t<string>('common:general.promoCode')}
              </Typography>
              <Typography variant="subtitle2" fontWeight={500}>
                {formatPrice(-totalDiscount, currency, false, true)}
              </Typography>
            </LeaderMenuItem>
          </Grid>
        )}
        {totalDonations > 0 && (
          <Grid item>
            <LeaderMenuItem>
              <Typography variant="subtitle2" fontWeight={500}>
                {t<string>('common:general.donation')}
              </Typography>
              <Typography variant="subtitle2" fontWeight={500}>
                {formatPrice(totalDonations, currency, false, false)}
              </Typography>
            </LeaderMenuItem>
          </Grid>
        )}
        {!!(
          paidTicketsCount &&
          (ticketsFeeCoverage === 'buyer-covers-fees' ||
            (ticketsFeeCoverage === 'buyer-can-decide' && isPaidSubscriptionFeeByBuyer))
        ) && (
          <Grid item>
            <LeaderMenuItem>
              <Grid container alignItems="center" sx={{ width: 'unset' }}>
                <Grid item>
                  <Typography variant="subtitle2" fontWeight={500}>
                    Tickets Fee {ticketsFeeSufix}
                  </Typography>
                </Grid>
              </Grid>
              <Typography variant="subtitle2" fontWeight={500}>
                {formatPrice(paidTicketsCount * 1, currency, false, false)}
              </Typography>
            </LeaderMenuItem>
          </Grid>
        )}
        {processingFee > 0 && (
          <Grid item>
            <LeaderMenuItem>
              <Grid container alignItems="center" sx={{ width: 'unset' }}>
                <Grid item>
                  <Typography variant="subtitle2" fontWeight={500}>
                    {t<string>('common:general.processingFee')} ({processingFeeLabel})
                  </Typography>
                </Grid>
                <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                  <div
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    className={classes.infoIcon}
                  >
                    <TooltipInfo
                      icon={ToolTipIcon}
                      placement={isMobile ? 'top-start' : 'left-start'}
                      textNode={
                        <Typography variant="caption" color="textPrimary">
                          {t<string>('creatingOrder.toolTips.processingFee')}
                        </Typography>
                      }
                    />
                  </div>
                </Grid>
              </Grid>
              <Typography variant="subtitle2" fontWeight={500}>
                {formatPrice(processingFee, currency, false, false)}
              </Typography>
            </LeaderMenuItem>
          </Grid>
        )}
        <Grid item>
          <LeaderMenuItem>
            <Typography variant="subtitle2">{t<string>('common:general.total')}</Typography>
            <Typography variant="subtitle2">{formatPrice(totalPrice, currency, true, true)}</Typography>
          </LeaderMenuItem>
        </Grid>
        {!!(
          (paidTicketsCount && ticketsFeeCoverage === 'host-covers-fees-disclosed') ||
          (ticketsFeeCoverage === 'buyer-can-decide' && !isPaidSubscriptionFeeByBuyer)
        ) && (
          <Grid item sx={{ paddingTop: '6px !important' }}>
            <Typography
              variant="body2"
              fontWeight={400}
              fontStyle="italic"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              A $1 per ticket fee has been paid by the host.{' '}
              <ToolTipIcon style={{ marginLeft: 2, cursor: 'pointer' }} onClick={handleOnClick} />
            </Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default OrderSummary;
